import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { scroller } from 'react-scroll';
import styled from 'styled-components';
import { Grid, Type } from '../../app/StyleGuide';
import { Button, PrimaryButton } from '../../components/Buttons';
import { CurriculumBrandBanner } from '../../components/CurriculumBrandBanner';
import ErrorMessage from '../../components/ErrorMessage';
import { Container, FixedHeaderContainer, FixedHeaderContent } from '../../components/Layout';
import LoadingState from '../../components/LoadingState';
import { CustomizationIndicator } from '../../components/SessionElements';
import useCurriculum from '../../hooks/useCurriculum';
import useModal from '../../hooks/useModal';
import sessionTimelineService from '../../services/sessionTimelineService';
import { ymdFromDate } from '../../utils/dateUtils';
import SessionHeader from './SessionHeader';
import SessionImageAndDetails from './SessionImageAndDetails';
import { DownloadPresentationMediaButton } from './components/DownloadPresentationMediaButton';
import { Session } from './models/session';
import { Timeline } from './models/timeline';
import PrintModal from './printableTimeline/PrintModal';
import PrintableTimeline, { PrintPreferences } from './printableTimeline/PrintableTimeline';
import SessionTimeline from './timeline/SessionTimeline';
import { SessionTimelineTab, SessionTimelineTabs } from './timeline/SessionTimelineElements';
import SessionTimelineSupplies from './timeline/SessionTimelineSupplies';
import { waitForImagesToLoad } from './utils/waitForImages';

const Pages = Object.freeze({
  LEADERGUIDE: 'leaderguide',
  SUPPLIES: 'supplies',
});

const CustomizationMessage = styled.div`
  font-size: ${Type.Scale._2};
  font-weight: ${Type.Weight.light};
  color: ${Type.Color.medium};
  margin: -${Grid._3} 0 ${Grid._4} 0;

  ${CustomizationIndicator} {
    margin-right: ${Grid._2};
  }
`;

const PrintContent = styled.div`
  display: none;

  @media print {
    display: block;
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    height: auto;
    overflow: visible;
    page-break-inside: auto;
    break-inside: auto;

    /* Ensure all content is visible for printing */
    & * {
      page-break-inside: auto;
      break-inside: auto;
      overflow: visible;
    }
  }
`;

const TimelineContainer = styled(Container)`
  @media print {
    display: none;
  }
`;

const transformPreviewTimeline = (timeline: Timeline) => ({
  ...timeline,
  viewPreference: 'all', // ensure the previewer starts with all the content viewable
});

export default function BibleStudySessionTimeline({ preview }: { preview: boolean }) {
  const [data, setData] = useState<{ isLoading: boolean; session?: Session; timeline?: Timeline; isError?: boolean }>({
    isLoading: true,
  });
  const [activePage, setActivePage] = useState<(typeof Pages)[keyof typeof Pages]>(Pages.LEADERGUIDE);
  const [scrollToData, setScrollToData] = useState<{ id: string; isFlexOption: boolean }>();
  const [printPreferences, setPrintPreferences] = useState<PrintPreferences>({
    viewPreference: 'all',
    fontSize: 12,
    printStyle: 'color',
    includeAgenda: true,
    media: true,
    suppliesOnly: false,
  });
  const { sessionId, issueId, timelineId, timelineCustomizationId } = useParams<{
    sessionId: string;
    issueId: string;
    timelineId: string;
    timelineCustomizationId: string;
  }>();
  const curriculum = useCurriculum();
  const curriculumId = data.session?.curriculumId || '';

  useEffect(() => {
    sessionTimelineService
      .getTeacherSessionAndTimeline(issueId, sessionId, timelineId, timelineCustomizationId)
      .then(([session, timeline]: [Session, Timeline]) => {
        setData({ isLoading: false, session, timeline: preview ? transformPreviewTimeline(timeline) : timeline });
      })
      .catch((error: Error) => {
        // eslint-disable-next-line no-console
        console.error(error);
        setData({ isLoading: false, isError: true });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (scrollToData) {
      scroller.scrollTo(scrollToData.id, {
        smooth: true,
        duration: 500,
        offset: scrollToData.isFlexOption ? -90 : 0,
        containerId: 'session-timeline-container',
      });

      setScrollToData(undefined);
    }
  }, [activePage, scrollToData]);

  const scrollTo = (id: string, isFlexOption: boolean) => {
    setScrollToData({ id: `contents-${id}`, isFlexOption });
    setActivePage(Pages.LEADERGUIDE);
  };

  const handlePrintSubmit = (values: PrintPreferences) => {
    setPrintPreferences({ ...values, viewPreference: values.viewPreference || 'all' });

    requestAnimationFrame(() => {
      const printRoot = document.querySelector('[data-qa-hook="print-content"]') as HTMLElement | null;

      const runPrint = () => {
        requestAnimationFrame(() => {
          const originalTitle = document.title;

          document.title = curriculum.brand.name + ' - ' + curriculum.ageCategory + ' - ' + data.session?.name;

          const handleAfterPrint = () => {
            document.title = originalTitle;
            window.removeEventListener('afterprint', handleAfterPrint);
          };
          window.addEventListener('afterprint', handleAfterPrint);

          if (navigator.userAgent.toLowerCase().includes('safari')) {
            document.execCommand('print', false, undefined);
          } else {
            window.print();
          }
        });
      };

      if (printRoot) {
        waitForImagesToLoad(printRoot).finally(runPrint);
      } else {
        runPrint();
      }
    });
  };

  const [modal, openModal] = useModal((type: string, _: unknown, onDismiss: () => void) => {
    if (!type) return;

    return <PrintModal initialValues={printPreferences} handleDismiss={onDismiss} onSubmit={handlePrintSubmit} />;
  });

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (activePage === Pages.LEADERGUIDE && (event.ctrlKey || event.metaKey) && event.key === 'p') {
        event.preventDefault();
        openModal('print');
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [openModal]);

  const handleChangeTab = (tab: (typeof Pages)[keyof typeof Pages]) => {
    setPrintPreferences({ ...printPreferences, suppliesOnly: tab === Pages.SUPPLIES });
    setActivePage(tab);
  };

  return (
    <FixedHeaderContainer fullViewport>
      <div>
        {!preview && (
          <SessionHeader
            brandName={`${curriculum.brand.name}: ${curriculum.ageCategory}`}
            sessionName={data.session ? data.session.name : 'Loading...'}
            isLoading={data.isLoading}
          />
        )}
      </div>

      <FixedHeaderContent id="session-timeline-container">
        <CurriculumBrandBanner brandCode={curriculum.brand.code} ageCategory={curriculum.ageCategory} />
        {data.isLoading ? (
          <TimelineContainer>
            <LoadingState />
          </TimelineContainer>
        ) : data.isError || !data.session ? (
          <TimelineContainer>
            <ErrorMessage />
          </TimelineContainer>
        ) : (
          <>
            <TimelineContainer>
              <SessionImageAndDetails
                session={data.session}
                preDescriptionDetails={
                  timelineCustomizationId ? (
                    <CustomizationMessage>
                      <CustomizationIndicator />
                      This session has been customized by one of your church admins
                    </CustomizationMessage>
                  ) : null
                }
              />
            </TimelineContainer>

            <SessionTimelineTabs>
              <SessionTimelineTab
                page={Pages.LEADERGUIDE}
                activePage={activePage}
                onTabClick={() => handleChangeTab(Pages.LEADERGUIDE)}
              >
                <span className="fas fa-chalkboard-teacher fa-fw hidden-xs"></span>Leader Guide
              </SessionTimelineTab>
              <SessionTimelineTab
                page={Pages.SUPPLIES}
                activePage={activePage}
                onTabClick={() => handleChangeTab(Pages.SUPPLIES)}
              >
                <span className="fas fa-box fa-fw hidden-xs"></span>Supplies
              </SessionTimelineTab>
            </SessionTimelineTabs>

            {activePage === Pages.LEADERGUIDE && (
              <SessionTimeline
                curriculumName={curriculum.ageCategory}
                timeline={data.timeline!}
                session={data.session}
                title={data.timeline?.name || 'Leader Guide'}
                subtitle="A step by step leader guide to lead and facilitate the session."
                actionButtons={
                  <>
                    <DownloadPresentationMediaButton
                      curriculumId={curriculumId}
                      timelineId={timelineId}
                      timelineCustomizationId={timelineCustomizationId}
                      curriculumName={curriculum.brand.name}
                      curriculumAgeCategory={curriculum.ageCategory}
                      sessionScheduledDate={ymdFromDate(data.session?.scheduledDate)}
                      timelineName={data.timeline?.name}
                      ButtonComponent={PrimaryButton}
                    />
                    <Button onClick={() => openModal('print')}>Print Leader Guide</Button>
                    {modal}
                  </>
                }
                config={{ stickyOffset: 0, scrollContainerId: 'session-timeline-container', topOffset: 68 }}
                useViewPreferences
              >
                <PrintContent data-qa-hook="print-content">
                  <PrintableTimeline printPreferences={printPreferences} />
                </PrintContent>
              </SessionTimeline>
            )}
            {activePage === Pages.SUPPLIES && (
              <SessionTimelineSupplies
                curriculumName={curriculum.ageCategory}
                timeline={data.timeline!}
                session={data.session!}
                onViewClick={scrollTo}
                onPrintClick={() => handlePrintSubmit({ ...printPreferences, suppliesOnly: true })}
              >
                <PrintContent data-qa-hook="print-content">
                  <PrintableTimeline printPreferences={printPreferences} />
                </PrintContent>
              </SessionTimelineSupplies>
            )}
          </>
        )}
      </FixedHeaderContent>
    </FixedHeaderContainer>
  );
}
